'use client'

import { usePathname } from 'next/navigation'
import { useCallback, useEffect, useRef, useState } from 'react'
import { usePathnameChanged } from 'ui/hooks'
import { useRefUpdated } from 'ui/hooks/useRefUpdated'
import { useGtmEvents } from './useGtmEvents'

/**
 * How long the first page_view waits for the CMP to report. Resolved as soon as
 * it answers — this is only the cap for a CMP that is slow or never loads, so it
 * trades a little latency for not mislabelling a returning visitor.
 */
const CONSENT_SETTLE_TIMEOUT_MS = 1000

/**
 * How long a pageview waits for `<title>` to catch up with the new route. The
 * App Router applies metadata as `generateMetadata` resolves, which is not
 * ordered against the effect that fires this event, so reading `document.title`
 * straight away can report the outgoing page's title. Capped so a route that
 * genuinely reuses its predecessor's title still gets tracked.
 */
const TITLE_SETTLE_TIMEOUT_MS = 500

/**
 * Calls back with `document.title` once it differs from `previousTitle`, or with
 * whatever it holds when the cap expires. Returns a teardown, or nothing when it
 * resolved synchronously. `document.head` is observed rather than the `<title>`
 * element because React may replace that element instead of mutating its text.
 */
const onPageTitleSettled = (previousTitle: string, onSettled: (title: string) => void) => {
  if (document.title !== previousTitle) {
    onSettled(document.title)

    return
  }

  const settle = () => {
    observer.disconnect()
    window.clearTimeout(timeoutId)
    onSettled(document.title)
  }

  const observer = new MutationObserver(() => {
    if (document.title !== previousTitle) {
      settle()
    }
  })

  observer.observe(document.head, { characterData: true, childList: true, subtree: true })

  const timeoutId = window.setTimeout(settle, TITLE_SETTLE_TIMEOUT_MS)

  return () => {
    observer.disconnect()
    window.clearTimeout(timeoutId)
  }
}

export const useGTMPageViewTracker = () => {
  const { trackPageView, isInitialized, dataLayerManager, consentManager, config, consentState } =
    useGtmEvents()
  const currentPathname = usePathname()
  const pathnameToBeTracked = useRef(currentPathname)
  /** `null` until the first pageview — there is no outgoing title to wait past. */
  const lastTrackedTitle = useRef<string | null>(null)
  const cancelTitleWait = useRef<(() => void) | undefined>(undefined)

  /**
   * The CMP loads over the network, while hydration does not — so the first
   * page_view would otherwise fire while consent is still at its provisional
   * `denied` default. For a visitor who consented on an earlier visit that means
   * their GA4 tag fires with `analytics_storage: denied` and sends a cookieless
   * ping instead of a real hit, even though consent was already stored.
   */
  const [isConsentSettled, setIsConsentSettled] = useState(false)

  useEffect(() => {
    if (!isInitialized || isConsentSettled) {
      return
    }

    // No CMP configured reports settled immediately — nothing will ever answer.
    if (consentManager?.hasReceivedConsentSignal() ?? true) {
      setIsConsentSettled(true)
      return
    }

    const timeoutId = window.setTimeout(() => setIsConsentSettled(true), CONSENT_SETTLE_TIMEOUT_MS)

    return () => window.clearTimeout(timeoutId)
    // `consentState` re-runs this the moment the CMP answers.
  }, [isInitialized, isConsentSettled, consentManager, consentState])

  const trackPage = useCallback(
    (pathname?: string | null) => {
      // `sendPageView: false` hands page_view to the merchant's own GA4 config
      // tag, which otherwise fires a second one on every initial load.
      if (
        !pathname ||
        !isInitialized ||
        !dataLayerManager ||
        !isConsentSettled ||
        !config?.sendPageView ||
        !pathnameToBeTracked.current
      ) {
        return
      }

      const push = (title: string) => {
        try {
          const isTracked = trackPageView({
            page_location: window.location.href,
            page_title: title
          })

          if (isTracked) {
            // Clear in order not to track this page
            pathnameToBeTracked.current = ''
            lastTrackedTitle.current = title

            if (config?.isDebugMode) {
              console.log('Tracked page event: ', pathname, title)
            }
          }
        } catch (e) {
          if (config?.isDebugMode) {
            const errorMessage = e instanceof Error ? e.message : 'Unknown error'

            console.error('Error tracking page view: ', errorMessage)
          }
        }
      }

      // A navigation that lands while the previous one is still waiting on its
      // title supersedes it — only the page the visitor is on gets reported.
      cancelTitleWait.current?.()
      cancelTitleWait.current = undefined

      const previousTitle = lastTrackedTitle.current

      // The first pageview renders server-side, so `<title>` is already correct.
      if (previousTitle === null) {
        push(document.title)

        return
      }

      cancelTitleWait.current = onPageTitleSettled(previousTitle, push)
    },
    [
      config?.isDebugMode,
      config?.sendPageView,
      dataLayerManager,
      isConsentSettled,
      isInitialized,
      trackPageView
    ]
  )

  const refTrackPage = useRefUpdated(trackPage)

  useEffect(() => () => cancelTitleWait.current?.(), [])

  useEffect(() => {
    if (isConsentSettled) {
      refTrackPage.current?.(pathnameToBeTracked.current)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- take ref as deps
  }, [isConsentSettled])

  usePathnameChanged(({ data, meta: { isPathChanged } }) => {
    if (isPathChanged) {
      pathnameToBeTracked.current = data.pathname
    }

    refTrackPage.current?.(data.pathname)
  })
}
